/**
 * Exam preview — own tab (/instructor/exams/:examId/preview).
 * Shows the exam the way a learner meets it (intro card: instructions, time limit, attempts, pass
 * mark) followed by every question (QuestionPreview), with the answer key on or off, and the
 * exam's rules summarised in a resizable side pane.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { DashboardLayout } from '../../components/DashboardLayout';
import { useTabLabel } from '../../components/shell/TabsContext';
import RecordPage, { FormSection } from '../../components/shell/RecordPage';
import { StatTiles } from '../../components/shell/Page';
import { StatusBadge } from '../../components/datagrid/DataGrid';
import QuestionPreview, { typeLabel } from '../../components/questions/QuestionPreview';
import RichHtml from '../../components/richtext/RichText';
import { toast } from 'sonner';
import { Eye, EyeSlash, PencilSimple, ChartBar, Timer, Repeat, Target, ListNumbers, Shuffle, GearSix, Info, Printer } from '@phosphor-icons/react';

function Rule({ icon: Icon, label, value, on }) {
  return (
    <div className="flex items-start gap-2 text-[13px]">
      <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${on === false ? 'text-muted-foreground/60' : 'text-shell-primary'}`} />
      <div className="min-w-0"><div className="text-[11px] text-muted-foreground">{label}</div><div className={on === false ? 'text-muted-foreground' : ''}>{value}</div></div>
    </div>
  );
}

export default function ExamPreview({ learnerView = false }) {
  const { examId } = useParams();
  const { api } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAnswers, setShowAnswers] = useState(!learnerView);
  const [rulesOpen, setRulesOpen] = useState(!learnerView);
  useTabLabel(data?.exam?.title ? `Preview: ${data.exam.title}` : 'Preview exam');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/exams/${examId}/preview`);
      setData(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load the exam');
    } finally {
      setLoading(false);
    }
  }, [api, examId]);
  useEffect(() => { load(); }, [load]);

  const exam = data?.exam;
  const settings = exam?.settings || {};
  const questions = data?.questions || [];
  const isRandom = !!exam?.random_selection;

  const side = exam ? {
    open: rulesOpen, onOpenChange: setRulesOpen, title: 'Exam rules', subtitle: 'What the learner will experience', icon: GearSix, width: 30, storageKey: 'pane-exam-rules', testid: 'exam-rules-pane',
    children: (
      <div className="space-y-3">
        <Rule icon={ListNumbers} label="Questions" value={isRandom ? `${data.question_count || 0} drawn at random from a pool of ${data.pool_size}` : `${questions.length} · ${data.total_points} marks in total`} />
        <Rule icon={Timer} label="Time limit" value={settings.time_limit_minutes ? `${settings.time_limit_minutes} minutes` : 'No time limit'} on={!!settings.time_limit_minutes} />
        <Rule icon={Repeat} label="Attempts allowed" value={settings.max_attempts ?? 1} />
        <Rule icon={Target} label="Pass mark" value={`${settings.passing_score ?? 60}%`} />
        <Rule icon={Shuffle} label="Shuffle" value={[settings.shuffle_questions && 'questions', settings.shuffle_options && 'options'].filter(Boolean).join(' and ') || 'Off — fixed order'} on={!!(settings.shuffle_questions || settings.shuffle_options)} />
        <Rule icon={Eye} label="After submitting" value={[settings.show_results_immediately ? 'Score shown immediately' : 'Score withheld', settings.show_correct_answers ? 'correct answers shown' : 'answers hidden'].join(' · ')} />
        <Rule icon={Info} label="Hints" value={settings.show_hints ? 'Shown to learners' : 'Hidden'} on={!!settings.show_hints} />
        {(settings.availability_start || settings.availability_end) && (
          <Rule icon={Timer} label="Available" value={`${settings.availability_start ? new Date(settings.availability_start).toLocaleString() : '—'} → ${settings.availability_end ? new Date(settings.availability_end).toLocaleString() : '—'}`} />
        )}
        <p className="field-hint pt-2 border-t border-border">This is a preview — nothing is recorded. Use <span className="font-medium">Hide answers</span> to see exactly what a learner sees.</p>
      </div>
    ),
  } : null;

  return (
    <DashboardLayout>
      <RecordPage
        title={exam ? exam.title : 'Preview exam'}
        subtitle={exam ? `${learnerView ? 'Student preview' : 'Preview'} · ${exam.assessment_type} · ${data.course_title || 'no course'}` : 'Loading…'}
        badge={exam && <StatusBadge value={exam.is_published ? 'published' : 'draft'} />}
        onBack={() => (learnerView ? window.history.back() : navigate('/instructor/exams'))}
        onRefresh={load}
        refreshing={loading}
        loading={loading && !data}
        side={side}
        findable
        primary={learnerView ? [
          { key: 'answers', label: showAnswers ? 'Hide answers' : 'Show answers', icon: showAnswers ? EyeSlash : Eye, onClick: () => setShowAnswers((v) => !v), testid: 'preview-toggle-answers' },
        ] : [
          { key: 'answers', label: showAnswers ? 'Hide answers' : 'Show answers', icon: showAnswers ? EyeSlash : Eye, onClick: () => setShowAnswers((v) => !v), testid: 'preview-toggle-answers' },
          { key: 'edit', label: 'Edit exam', icon: PencilSimple, variant: 'outline', onClick: () => navigate(`/instructor/exams/${examId}`), testid: 'preview-edit-exam' },
        ]}
        secondary={learnerView ? [] : [
          { key: 'rules', label: rulesOpen ? 'Hide rules' : 'Exam rules', icon: GearSix, onClick: () => setRulesOpen((v) => !v) },
          { key: 'report', label: 'Report', icon: ChartBar, onClick: () => navigate(`/instructor/exams/${examId}/report`) },
          { key: 'print', label: 'Print', icon: Printer, onClick: () => window.print() },
        ]}
        maxWidth="none"
      >
        {exam && (
          <>
            <StatTiles items={[
              { label: 'Questions', value: isRandom ? `${data.question_count || 0} / ${data.pool_size}` : questions.length, icon: ListNumbers },
              { label: 'Total marks', value: data.total_points, icon: Target, iconClass: 'bg-violet-100 text-violet-700' },
              { label: 'Time limit', value: settings.time_limit_minutes ? `${settings.time_limit_minutes} min` : '—', icon: Timer, iconClass: 'bg-sky-100 text-sky-700' },
              { label: 'Pass mark', value: `${settings.passing_score ?? 60}%`, icon: Target, iconClass: 'bg-emerald-100 text-emerald-700' },
            ]} />

            {/* Intro card as the learner sees it */}
            <FormSection title="Start screen" icon={Info} bodyClassName="p-4">
              <div className="max-w-3xl space-y-2">
                <h2 className="font-heading text-lg font-bold">{exam.title}</h2>
                {exam.description && <RichHtml html={exam.description} className="text-[13px] text-muted-foreground" />}
                {exam.instructions ? (
                  <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-[13px] text-amber-900">
                    <div className="font-medium mb-0.5">Instructions</div>
                    <RichHtml html={exam.instructions} />
                  </div>
                ) : (
                  <p className="field-hint">No instructions set — learners go straight to question 1.</p>
                )}
                <p className="text-xs text-muted-foreground">
                  {isRandom ? `${data.question_count || 0} questions` : `${questions.length} questions`} · {data.total_points} marks
                  {settings.time_limit_minutes ? ` · ${settings.time_limit_minutes} minutes` : ' · no time limit'} · {settings.max_attempts ?? 1} attempt{(settings.max_attempts ?? 1) === 1 ? '' : 's'}
                </p>
              </div>
            </FormSection>

            {isRandom && (
              <div className="rounded border border-sky-200 bg-sky-50 px-3 py-2 text-[13px] text-sky-900 flex items-start gap-2">
                <Shuffle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>This exam draws <strong>{data.question_count || 0}</strong> questions at random from the pool below ({data.pool_size} matching questions). Each learner gets a different selection.</span>
              </div>
            )}

            {questions.length === 0 ? (
              <FormSection title="Questions" icon={ListNumbers} bodyClassName="p-6">
                <p className="text-sm text-muted-foreground text-center">No questions in this exam yet — add some in the editor.</p>
              </FormSection>
            ) : (
              <div className="space-y-3" data-testid="exam-preview-questions">
                {questions.map((q, i) => (
                  <div key={q.id} className="max-w-4xl" data-find={`Q${i + 1}`}>
                    <div className="flex items-center gap-2 px-1 pb-1 text-xs text-muted-foreground">
                      <span className="font-semibold text-foreground">Q{i + 1}</span>
                      <span>{typeLabel(q.question_type)}</span>
                      <span className="ml-auto">{q.points ?? 1} mark{q.points === 1 ? '' : 's'}</span>
                    </div>
                    <QuestionPreview
                      question={q}
                      showAnswers={showAnswers}
                      categoryName={data.categories?.[q.category_id]}
                      courseName={data.course_title}
                      compact
                    />
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </RecordPage>
    </DashboardLayout>
  );
}
